import { lazy, useMemo } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";

import { useMenuNavigationProgram } from "@repo/ui";
import {
  ROUTESWEB,
  getFirstVisibleWebBottomNavTab,
  getMenuNavigationFlags,
  WEB_BOTTOM_NAV_ROUTE_PATHS,
} from "@repo/utils";
import { useIsAuthenticated } from "../store/useAuthStore";

import { AuthGuard } from "../components/auth/AuthGuard";
import { DeepLinkSyncGuard } from "../components/deeplink/DeepLinkSyncGuard";
import type { EnrolledProgram, ModuleDataDto } from "../store/useAuthStore";
import { useAuthStore } from "../store/useAuthStore";
import { initializeApi } from "../lib/setupApi";
import { Loadable, LoadableWithProps } from "./utils";
import { AINA_AGENT_MENTOR, AINA_AGENT_VOICE } from "../aina/AinaHostBridge";
import { AinaAppLazy } from "../aina/AinaAppLazy";

function getModuleDataDto(): ModuleDataDto {
  const authState = useAuthStore.getState();
  return {
    accessToken: authState.token ?? null,
    refreshToken: authState.refreshToken ?? null,
    accountsData: authState.accountsData ?? null,
    profilesData: authState.profilesData ?? null,
    enrolledPrograms: authState.enrolledPrograms ?? null,
    setSessionData: (
      _tenantId?: string | null,
      _subtenantId?: number | null,
      programs?: EnrolledProgram[] | null,
    ) => {
      if (programs === undefined) return;
      useAuthStore.setState((prev) => ({
        ...prev,
        enrolledPrograms: programs && Array.isArray(programs) ? programs : null,
      }));
    },
  };
}

function getLogOut(): void {
  const authState = useAuthStore.getState();
  return authState.logout();
}

/** Re-init self-learn API headers when host enrollment changes (profile switch, etc.). */
function subscribeEnrolledProgramsChange(onChange: () => void): () => void {
  return useAuthStore.subscribe((state, prev) => {
    const nextPrimary =
      state.enrolledPrograms?.find((p) => p.isDefault) ??
      state.enrolledPrograms?.[0];
    const prevPrimary =
      prev.enrolledPrograms?.find((p) => p.isDefault) ??
      prev.enrolledPrograms?.[0];
    const nextId = nextPrimary?.program_id;
    const prevId = prevPrimary?.program_id;
    if (nextId !== prevId || state.enrolledPrograms !== prev.enrolledPrograms) {
      onChange();
    }
  });
}

function DoubtsAppWithData() {
  return LoadableWithProps(DoubtsApp, {
    env: import.meta.env.MODE ?? "production",
    restoreHostApi: () => initializeApi(),
    dataDTO: getModuleDataDto(),
    handleLogOut: getLogOut,
  });
}

function SelfLearnAppWithData() {
  return LoadableWithProps(SelfLearnApp, {
    env: import.meta.env.MODE ?? "production",
    restoreHostApi: () => initializeApi(),
    dataDto: getModuleDataDto(),
    getModuleDataDto,
    subscribeEnrolledProgramsChange,
    handleLogOut: getLogOut,
  });
}

function TestsAppWithData() {
  return LoadableWithProps(TestsApp, {
    env: import.meta.env.MODE ?? "production",
    restoreHostApi: () => initializeApi(),
    dataDto: getModuleDataDto(),
    handleLogOut: getLogOut,
  });
}

function AinaVoiceAppWithData() {
  return LoadableWithProps(AinaAppLazy, {
    env: import.meta.env.MODE ?? "production",
    restoreHostApi: () => initializeApi(),
    dataDTO: getModuleDataDto(),
    agent: AINA_AGENT_VOICE,
  });
}

function AinaMentorAppWithData() {
  return LoadableWithProps(AinaAppLazy, {
    env: import.meta.env.MODE ?? "production",
    restoreHostApi: () => initializeApi(),
    dataDTO: getModuleDataDto(),
    agent: AINA_AGENT_MENTOR,
  });
}

/* commented as it is failing the build
function LiveClassesAppWithData() {
  return LoadableWithProps(LiveClassesApp, {
    env: import.meta.env.MODE ?? "production",
    restoreHostApi: () => initializeApi(),
    dataDto: getModuleDataDto(),
  });
}
*/

const RootRedirect = () => {
  const isAuthenticated = useIsAuthenticated();
  const { gradeId, examIds, verticalId } = useMenuNavigationProgram();
  const flags = useMemo(
    () => getMenuNavigationFlags(gradeId, examIds, verticalId),
    [gradeId, examIds, verticalId],
  );
  const signedInHome =
    WEB_BOTTOM_NAV_ROUTE_PATHS[getFirstVisibleWebBottomNavTab(flags)];

  return isAuthenticated ? (
    <Navigate to={signedInHome} replace />
  ) : (
    <Navigate to={`/${ROUTESWEB.AUTH.INTRO}`} replace />
  );
};

/** Unknown paths: send guests to login, signed-in users to first allowed bottom-nav tab. */
const UnknownRouteFallback = () => {
  const isAuthenticated = useIsAuthenticated();
  const { gradeId, examIds, verticalId } = useMenuNavigationProgram();
  const flags = useMemo(
    () => getMenuNavigationFlags(gradeId, examIds, verticalId),
    [gradeId, examIds, verticalId],
  );
  const signedInFallback =
    WEB_BOTTOM_NAV_ROUTE_PATHS[getFirstVisibleWebBottomNavTab(flags)];

  return (
    <Navigate
      to={isAuthenticated ? signedInFallback : `/${ROUTESWEB.AUTH.LOGIN}`}
      replace
    />
  );
};

const AuthLayout = lazy(() =>
  import("../layouts/AuthLayout").then((module) => ({
    default: module.AuthLayout,
  })),
);
const DashboardLayout = lazy(() =>
  import("../layouts/DashboardLayout").then((module) => ({
    default: module.DashboardLayout,
  })),
);
const AgoraLayout = lazy(() =>
  import("../layouts/AgoraLayout").then((module) => ({
    default: module.AgoraLayout,
  })),
);

const OnboardingPage = lazy(() =>
  import("../pages/auth/onboarding").then((module) => ({
    default: module.OnboardingPage,
  })),
);

const PlaylistDetailPage = lazy(() =>
  import("../pages/liveclasses/playlist/PlaylistDetailPage").then((module) => ({
    default: module.PlaylistDetailPage,
  })),
);
const VideoPlayerPage = lazy(() =>
  import("../pages/liveclasses/playlist/VideoPlayerPage").then((module) => ({
    default: module.VideoPlayerPage,
  })),
);
const SignupPage = lazy(() =>
  import("../pages/auth/SignUp/SignupPage").then((module) => ({
    default: module.default,
  })),
);
const DashboardPage = lazy(() =>
  import("../pages/dashboard/DashboardPage").then((module) => ({
    default: module.DashboardPage,
  })),
);
const ToDoActivitiesPage = lazy(() =>
  import("../pages/dashboard/activities/ToDoActivitiesPage").then((module) => ({
    default: module.ToDoActivitiesPage,
  })),
);
const AgoraLandingPage = lazy(() =>
  import("../pages/agora").then((module) => ({
    default: module.AgoraLandingPage,
  })),
);
const LiveClassesHome = lazy(() =>
  import("../pages/liveclasses/LiveClassesHome").then((module) => ({
    default: module.default,
  })),
);
const DirectClassesList = lazy(() =>
  import("../pages/liveclasses/DirectClassesList").then((module) => ({
    default: module.default,
  })),
);
const NotFoundPage = lazy(() =>
  import("../pages/NotFoundPage").then((module) => ({
    default: module.NotFoundPage,
  })),
);
const ReportsPage = lazy(() =>
  import("../pages/reports/ReportsPage").then((module) => ({
    default: module.ReportsPage,
  })),
);

const ReportTestingPage = lazy(() =>
  import("../pages/reports/ReportTestingPage").then((module) => ({
    default: module.ReportTestingPage,
  })),
);

const ContactUsPage = lazy(() =>
  import("../pages/contact-us/ContactUsPage").then((module) => ({
    default: module.ContactUsPage,
  })),
);

const SubscriptionsHomePage = lazy(() =>
  import("../pages/subscriptions/SubscriptionsHomePage").then((module) => ({
    default: module.default,
  })),
);
const ReviewOrderPage = lazy(() =>
  import("../pages/subscriptions/ReviewOrderPage").then((module) => ({
    default: module.default,
  })),
);
const PaymentStatusPage = lazy(() =>
  import("../pages/subscriptions/payment-status-page/index").then((module) => ({
    default: module.default,
  })),
);
const YoutubeLiveClassPage = lazy(() =>
  import("../pages/liveclasses/youtube-live-class").then((module) => ({
    default: module.YoutubeLiveClassPage,
  })),
);

const AssignmentInstructionsPage = lazy(() =>
  import("../pages/liveclasses/courseOverview/pages/AssignmentInstructions").then(
    (module) => ({
      default: module.AssignmentInstructions,
    }),
  ),
);

const AssignmentExamPage = lazy(() =>
  import("../pages/liveclasses/courseOverview/pages/AssignmentExam").then(
    (module) => ({
      default: module.AssignmentExam,
    }),
  ),
);

const DoubtsApp = lazy(() =>
  import("@repo/doubts-web").then((module) => ({
    default: module.DoubtsApp,
  })),
);
const SelfLearnApp = lazy(() =>
  import("@repo/self-learn-web").then((module) => ({
    default: module.SelfLearnApp,
  })),
);
const TestsApp = lazy(() =>
  import("@repo/tests-web").then((module) => ({
    default: module.TestsApp,
  })),
);
const PhoneLoginPage = lazy(() =>
  import("../pages/auth/phoneLoginPage").then((module) => ({
    default: module.PhoneLoginPage,
  })),
);
const ForgotPasswordPage = lazy(() =>
  import("../pages/auth/ForgotPassword/ForgotPasswordPage").then((module) => ({
    default: module.default,
  })),
);
const ResetPasswordPage = lazy(() =>
  import("../pages/auth/ResetPassword/ResetPasswordPage").then((module) => ({
    default: module.default,
  })),
);
const VerifyOtpPage = lazy(() =>
  import("../pages/auth/verifyOtpPage").then((module) => ({
    default: module.VerifyOtpPage,
  })),
);
const UsernameLoginPage = lazy(() =>
  import("../pages/auth/usernameLoginPage").then((module) => ({
    default: module.UsernameLoginPage,
  })),
);
const MultipleAccountsPage = lazy(() =>
  import("../pages/auth/MultipleAccounts/MultipleAccountsPage").then(
    (module) => ({ default: module.default }),
  ),
);

const CourseOverviewPage = lazy(() =>
  import("../pages/liveclasses/courseOverview/CourseOverviewPage").then(
    (module) => ({
      default: module.CourseOverviewPage,
    }),
  ),
);

const VideoRewatchScreen = lazy(() =>
  import("../pages/liveclasses/courseOverview/components/videoRewatch/VideoRewatchPage").then(
    (module) => ({
      default: module.VideoRewatchPage,
    }),
  ),
);

const SmartBatchingHomePage = lazy(() =>
  import("../pages/smart-batching/SmartBatchingHomePage/index").then(
    (module) => ({
      default: module.default,
    }),
  ),
);
const BatchConfirmationPage = lazy(() =>
  import("../pages/smart-batching/BatchConfirmationPage/index").then(
    (module) => ({
      default: module.default,
    }),
  ),
);

const ReferAndEarnPage = lazy(() =>
  import("../pages/refer-and-earn/ReferAndEarnPage").then((module) => ({
    default: module.ReferAndEarnPage,
  })),
);
const MyReferralsPage = lazy(() => import("../pages/my-referrals"));
const ReferAndEarnFAQPage = lazy(() =>
  import("../pages/refer-and-earn/ReferAndEarnFAQPage").then((module) => ({
    default: module.ReferAndEarnFAQPage,
  })),
);
const IntroPages = lazy(() =>
  import("../pages/intro/IntroPages").then((module) => ({
    default: module.default,
  })),
);

const ProfileSection = lazy(() =>
  import("../pages/profile/layout/ProfileSection").then((module) => ({
    default: module.default,
  })),
);
const ProfilePage = lazy(() =>
  import("../pages/profile").then((module) => ({
    default: module.default,
  })),
);
const ChangePhoneNumber = lazy(() =>
  import("../pages/profile/UpdatePhoneNumber").then((module) => ({
    default: module.default,
  })),
);

const InstructionsPage = lazy(() =>
  import("../pages/profile/InstructionsPage").then((module) => ({
    default: module.default,
  })),
);
const TermsAndPolicyPage = lazy(() =>
  import("../pages/profile/TermsAndPolicyPage").then((module) => ({
    default: module.default,
  })),
);
const VerifyAccountPage = lazy(() =>
  import("../pages/profile/DeleteProfile/FreeUserDeleteAccount/pages/VerifyAccountPage/VerifyAccountPage").then(
    (module) => ({
      default: module.default,
    }),
  ),
);

const DeleteAccountConfirmationPage = lazy(() =>
  import("../pages/profile/DeleteProfile/FreeUserDeleteAccount/pages/DeleteAccountConfirmationPage/DeleteAccountConfirmationPage").then(
    (module) => ({
      default: module.default,
    }),
  ),
);

const AdmissionIdPage = lazy(() =>
  import("../pages/auth/ScaLogin/AdmissionIDFlow/AdmissionId").then(
    (module) => ({
      default: module.default,
    }),
  ),
);
const AuthChangePhoneInstructionsPage = lazy(() =>
  import("../pages/auth/ScaLogin/ChangePhone/ChangePhoneInstructionsPage").then(
    (m) => ({ default: m.default }),
  ),
);
const AuthChangePhoneNewNumberPage = lazy(() =>
  import("../pages/auth/ScaLogin/ChangePhone/ChangePhoneNewNumberPage").then(
    (m) => ({
      default: m.default,
    }),
  ),
);
const AuthChangePhoneVerifyPage = lazy(() =>
  import("../pages/auth/ScaLogin/ChangePhone/ChangePhoneVerifyPage").then(
    (m) => ({
      default: m.default,
    }),
  ),
);
export const router = createBrowserRouter([
  {
    path: "/",
    element: <RootRedirect />,
    errorElement: Loadable(NotFoundPage),
  },
  {
    path: ROUTESWEB.AUTH.INTRO,
    element: Loadable(IntroPages),
  },
  {
    element: <AuthGuard requireAuth={false} />,
    children: [
      {
        element: Loadable(AuthLayout),
        children: [
          {
            path: ROUTESWEB.AUTH.LOGIN,
            element: Loadable(OnboardingPage),
          },
          {
            path: ROUTESWEB.AUTH.PHONE_LOGIN,
            element: Loadable(PhoneLoginPage),
          },
          {
            path: ROUTESWEB.AUTH.OTP_VERIFICATION,
            element: Loadable(VerifyOtpPage),
          },
          {
            path: ROUTESWEB.AUTH.USERNAME_LOGIN,
            element: Loadable(UsernameLoginPage),
          },

          {
            path: ROUTESWEB.AUTH.FORGOT_PASSWORD,
            element: Loadable(ForgotPasswordPage),
          },
          {
            path: ROUTESWEB.AUTH.RESET_PASSWORD,
            element: Loadable(ResetPasswordPage),
          },
          {
            path: ROUTESWEB.AUTH.MULTIPLE_ACCOUNTS,
            element: Loadable(MultipleAccountsPage),
          },
          {
            path: ROUTESWEB.AUTH.SIGNUP,
            element: Loadable(SignupPage),
          },
          {
            path: ROUTESWEB.AUTH.ADMISSION_ID_FLOW,
            element: Loadable(AdmissionIdPage),
          },
          {
            path: ROUTESWEB.AUTH.CHANGE_PHONE_INSTRUCTIONS,
            element: Loadable(AuthChangePhoneInstructionsPage),
          },
          {
            path: ROUTESWEB.AUTH.CHANGE_PHONE_NEW_NUMBER,
            element: Loadable(AuthChangePhoneNewNumberPage),
          },
          {
            path: ROUTESWEB.AUTH.CHANGE_PHONE_VERIFY,
            element: Loadable(AuthChangePhoneVerifyPage),
          },
        ],
      },
    ],
  },

  {
    element: <AuthGuard requireAuth />,
    errorElement: Loadable(NotFoundPage),
    children: [
      {
        element: <DeepLinkSyncGuard />,
        children: [
          {
            element: Loadable(DashboardLayout),
            children: [
              {
                path: ROUTESWEB.APP.DASHBOARD,
                element: Loadable(DashboardPage),
              },
              {
                path: ROUTESWEB.APP.ACTIVITIES,
                element: Loadable(ToDoActivitiesPage),
              },
              {
                path: ROUTESWEB.APP.COURSES,
                element: Loadable(LiveClassesHome),
              },
              {
                path: ROUTESWEB.APP.CLASSES,
                element: Loadable(DirectClassesList),
              },
              {
                path: ROUTESWEB.APP.MASTERCLASSES,
                element: null,
              },
              {
                path: `${ROUTESWEB.APP.PLAYLIST_DETAIL}/:id`,
                element: Loadable(PlaylistDetailPage),
              },
              {
                path: `${ROUTESWEB.APP.DOUBTS}/*`,
                element: <DoubtsAppWithData />,
              },
              {
                path: `${ROUTESWEB.APP.SELF_LEARN}/*`,
                element: <SelfLearnAppWithData />,
              },
              {
                path: `${ROUTESWEB.APP.TESTS}/*`,
                element: <TestsAppWithData />,
              },
              {
                path: `${ROUTESWEB.APP.AINA_VOICE}/*`,
                element: <AinaVoiceAppWithData />,
              },
              {
                path: `${ROUTESWEB.APP.AINA_MENTOR}/*`,
                element: <AinaMentorAppWithData />,
              },
              {
                path: ROUTESWEB.APP.ASSIGNMENT_INSTRUCTIONS,
                element: Loadable(AssignmentInstructionsPage),
              },
              {
                path: ROUTESWEB.APP.ASSIGNMENT_EXAM,
                element: Loadable(AssignmentExamPage),
              },
              {
                path: `${ROUTESWEB.APP.LIVE_CLASSES}/*`,
                element: Loadable(LiveClassesHome),
              },
              {
                path: ROUTESWEB.APP.CONTACT_US,
                element: Loadable(ContactUsPage),
              },
              {
                path: ROUTESWEB.APP.REFER_AND_EARN,
                element: Loadable(ReferAndEarnPage),
              },
              {
                path: ROUTESWEB.APP.REFER_AND_EARN_TERMS,
                element: Loadable(TermsAndPolicyPage),
              },
              {
                path: ROUTESWEB.APP.REFER_AND_EARN_FAQ,
                element: Loadable(ReferAndEarnFAQPage),
              },
              {
                path: ROUTESWEB.APP.MY_REFERRALS,
                element: Loadable(MyReferralsPage),
              },
              {
                path: ROUTESWEB.APP.SUBSCRIPTIONS,
                element: Loadable(SubscriptionsHomePage),
              },
              {
                path: ROUTESWEB.APP.REVIEW_ORDER,
                element: Loadable(ReviewOrderPage),
              },
              {
                path: ROUTESWEB.APP.PAYMENT_STATUS,
                element: Loadable(PaymentStatusPage),
              },
              {
                path: ROUTESWEB.APP.REPORTS,
                element: Loadable(ReportsPage),
              },
              {
                path: ROUTESWEB.APP.VIDEO_PLAYER,
                element: Loadable(VideoPlayerPage),
              },
              {
                path: ROUTESWEB.APP.REPORT_TESTING,
                element: Loadable(ReportTestingPage),
              },
            ],
          },
          {
            path: ROUTESWEB.APP.CONTACT_US,
            element: Loadable(ContactUsPage),
          },
        ],
      },
    ],
  },
  {
    element: <AuthGuard requireAuth />,
    errorElement: Loadable(NotFoundPage),
    children: [
      {
        element: Loadable(AgoraLayout),
        children: [
          {
            path: ROUTESWEB.APP.AGORA,
            element: Loadable(AgoraLandingPage),
          },
        ],
      },
      {
        element: <DeepLinkSyncGuard />,
        children: [
          {
            element: Loadable(DashboardLayout),
            children: [
              {
                path: ROUTESWEB.APP.YOUTUBE_LIVECLASS,
                element: Loadable(YoutubeLiveClassPage),
              },
            ],
          },
        ],
      },
    ],
  },
  {
    element: <AuthGuard requireAuth />,
    errorElement: Loadable(NotFoundPage),
    children: [
      {
        element: <DeepLinkSyncGuard />,
        children: [
          {
            element: Loadable(DashboardLayout),
            children: [
              {
                path: `${ROUTESWEB.APP.COURSE_OVERVIEW}/:id`,
                element: Loadable(CourseOverviewPage),
              },
            ],
          },
        ],
      },
    ],
  },
  {
    element: <AuthGuard requireAuth />,
    children: [
      {
        element: <DeepLinkSyncGuard />,
        children: [
          {
            element: Loadable(DashboardLayout),
            children: [
              {
                path: "live-class/:id/rewatch",
                element: Loadable(VideoRewatchScreen),
              },
            ],
          },
        ],
      },
    ],
  },
  {
    element: <AuthGuard requireAuth />,
    errorElement: Loadable(NotFoundPage),
    children: [
      {
        element: <DeepLinkSyncGuard />,
        children: [
          {
            element: Loadable(DashboardLayout),
            children: [
              {
                path: ROUTESWEB.APP.SMART_BATCHING,
                element: Loadable(SmartBatchingHomePage),
              },
              {
                path: ROUTESWEB.APP.BATCH_CONFIRMATION,
                element: Loadable(BatchConfirmationPage),
              },
            ],
          },
        ],
      },
    ],
  },
  {
    element: <AuthGuard requireAuth />,
    errorElement: Loadable(NotFoundPage),
    children: [
      {
        element: Loadable(DashboardLayout),
        children: [
          {
            path: ROUTESWEB.APP.PROFILE,
            element: Loadable(ProfileSection),
            children: [
              { index: true, element: Loadable(ProfilePage) },
              {
                path: "delete-account",
                element: Loadable(InstructionsPage),
              },
              {
                path: "delete-account/verify",
                element: Loadable(VerifyAccountPage),
              },
              {
                path: "delete-account/confirm",
                element: Loadable(DeleteAccountConfirmationPage),
              },
            ],
          },
          {
            path: ROUTESWEB.APP.CHANGE_PHONE_NUMBER,
            element: Loadable(ChangePhoneNumber),
          },
          {
            path: ROUTESWEB.APP.INSTRUCTIONS_PAGE,
            element: Loadable(InstructionsPage),
          },
          {
            path: ROUTESWEB.APP.CHANGE_NUMBER_VERIFY,
            element: Loadable(VerifyAccountPage),
          },
          {
            path: ROUTESWEB.APP.TERMSANDPOLICYPAGE,
            element: Loadable(TermsAndPolicyPage),
          },
        ],
      },
    ],
  },
  {
    path: "*",
    element: <UnknownRouteFallback />,
  },
]);
